// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * The export layout.
 *
 * Every convention below was read out of the trainers' own source rather than out of a blog post,
 * because each of them fails in a way that is silent — a folder that is skipped, a caption that is
 * truncated, a column that is ignored — and a training run that quietly trained on nothing is the
 * worst possible outcome for a tool whose whole job is to prepare a training set.
 *
 * The shipped tree, and why each part is where it is:
 *
 *   images/                      <- point kohya's --train_data_dir HERE, not at the folder below
 *     10_concept/                <- sd-scripts parses this name: repeats, then class tokens
 *       photo.jpg                <- images sit DIRECTLY here; kohya's glob is not recursive
 *       photo.txt                <- one caption per image
 *       metadata.jsonl           <- Hugging Face `imagefolder` reads this from the image directory
 *   dataset.toml                 <- kohya's newer TOML path IGNORES the folder name entirely
 *   dedup-report.csv             <- every input image and what happened to it
 *   embeddings.f32 + .json       <- re-cluster later without re-embedding
 *   README.md                    <- the commands, with the numbers already filled in
 */

import type { Decision, Group, ImageRecord, DatasetOptions } from './types.ts';
import { hashToHex } from './image.ts';
import { conceptDirName, assignExportNames, splitExtension } from './naming.ts';

export const IMAGES_DIR = 'images';

/**
 * Extensions kohya's `glob_images` actually accepts.
 *
 * `.avif` and `.jxl` are appended by sd-scripts only when an optional Pillow plugin imports
 * successfully, so an AVIF in the export is a coin flip on the user's machine. Loraprep copies the
 * original bytes through untouched, so an unsupported input format is reported rather than
 * silently included.
 */
export const KOHYA_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'webp', 'bmp']);

export interface ExportPlan {
  readonly conceptDir: string;
  /** Kept image id -> the name it gets inside the concept directory. */
  readonly names: ReadonlyMap<number, string>;
  readonly caption: string;
  readonly repeats: number;
  /** Kept images whose extension kohya will not glob. Reported, never silently dropped. */
  readonly unsupported: readonly number[];
}

export function planExport(
  kept: readonly ImageRecord[],
  opts: DatasetOptions,
): ExportPlan {
  const conceptDir = conceptDirName(opts.repeats, opts.concept);
  const assigned = assignExportNames(kept.map((r) => r.path));
  const names = new Map<number, string>();
  const unsupported: number[] = [];
  kept.forEach((r, i) => {
    names.set(r.id, assigned[i]!);
    if (!KOHYA_EXTENSIONS.has(splitExtension(assigned[i]!).ext)) unsupported.push(r.id);
  });
  return {
    conceptDir,
    names,
    caption: captionText(opts),
    repeats: Math.max(1, Math.floor(opts.repeats) || 1),
    unsupported,
  };
}

/**
 * The caption written into every `.txt` and into `metadata.jsonl`.
 *
 * It is NEVER empty. sd-scripts reads a caption file with `assert len(lines) > 0`, so an empty
 * `.txt` does not fall back to the folder's class token — it aborts the training run outright.
 * When the user gives no caption the concept token is used, which is what the folder name would
 * have supplied anyway.
 *
 * It is also collapsed to ONE line, because without `--enable_wildcard` sd-scripts uses
 * `lines[0].strip()` and silently discards everything after the first newline.
 */
export function captionText(opts: DatasetOptions): string {
  const raw = opts.caption.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (raw.length > 0) return raw;
  const fallback = opts.concept.trim();
  return fallback.length > 0 ? fallback : 'concept';
}

/**
 * A caption file's bytes.
 *
 * UTF-8 with NO byte-order mark. sd-scripts opens caption files with `encoding="utf-8"` rather
 * than `utf-8-sig`, and `.strip()` does not remove U+FEFF — so a BOM survives into the caption as
 * a literal character glued to the front of the first comma-separated tag, which is exactly where
 * the trigger word goes. A BOM here would quietly poison the token the whole LoRA is trained to
 * respond to.
 */
export function captionBytes(caption: string): Uint8Array {
  return new TextEncoder().encode(caption + '\n');
}

/**
 * One `metadata.jsonl` line.
 *
 * `file_name` is required by Hugging Face's ImageFolder loader and is relative to the directory
 * holding this file. Every other key becomes a dataset column.
 *
 * BOTH `text` and `prompt` carry the caption, and that redundancy is deliberate: diffusers has no
 * single convention. `train_text_to_image_lora.py` defaults `--caption_column` to `text`, while
 * the DreamBooth LoRA scripts default it to `None` and HF's own `convert_to_imagefolder.py`
 * writes `prompt`. Emitting one of the two means half of diffusers' examples silently ignore the
 * captions and train every image on the same instance prompt.
 */
export function metadataLine(
  rec: ImageRecord,
  exportName: string,
  caption: string,
  groupSize: number,
): string {
  return (
    JSON.stringify({
      file_name: exportName,
      text: caption,
      prompt: caption,
      source_path: rec.path,
      width: rec.width,
      height: rec.height,
      sharpness: Math.round(rec.sharpness * 100) / 100,
      dhash: hashToHex(rec.hash),
      group_size: groupSize,
    }) + '\n'
  );
}

/**
 * `dataset.toml` for kohya's config path.
 *
 * Required rather than a nicety: with a TOML config, sd-scripts documents that "even if you name
 * the folder something like '5_cat', the number of repeats of the image and the class name will
 * not be reflected". `num_repeats` and `class_tokens` have to be stated explicitly, so a dataset
 * that only carried the numbered folder name would train with the wrong repeat count for anyone
 * using the config path.
 */
export function datasetToml(plan: ExportPlan): string {
  const esc = (s: string): string => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `# Generated by loraprep — https://loraprep.benrichardson.dev
#
# The folder name below encodes the same numbers, but sd-scripts ignores it when you pass a TOML
# config, so num_repeats and class_tokens are stated here explicitly.
#
#   accelerate launch train_network.py --dataset_config dataset.toml ...

[general]
shuffle_caption = false
caption_extension = ".txt"
keep_tokens = 1

[[datasets]]
resolution = 1024
batch_size = 1

  [[datasets.subsets]]
  image_dir = "${esc(`${IMAGES_DIR}/${plan.conceptDir}`)}"
  class_tokens = "${esc(plan.caption)}"
  num_repeats = ${plan.repeats}
`;
}

export const REPORT_HEADER = [
  'source_path',
  'verdict',
  'reason',
  'export_name',
  'group_id',
  'group_size',
  'duplicate_of',
  'dhash',
  'sharpness',
  'flatness',
  'width',
  'height',
  'bytes',
] as const;

export interface ReportInputs {
  readonly records: readonly ImageRecord[];
  readonly decisions: readonly Decision[];
  readonly groups: readonly Group[];
  readonly plan: ExportPlan;
}

/**
 * `dedup-report.csv` — the file that makes the tool auditable.
 *
 * Every input image gets exactly one row, kept or not. `tests/dataset.test.ts` asserts the row
 * count equals the input count, because the report is the only place a user can go to answer
 * "where did that photo go", and a report that omits the answer is worse than no report.
 */
export function* reportCsv(input: ReportInputs): Generator<string, void, void> {
  const byId = new Map(input.records.map((r) => [r.id, r]));
  const groupSize = new Map<number, number>();
  for (const g of input.groups) for (const m of g.members) groupSize.set(m, g.members.length);

  yield csvRow(REPORT_HEADER as readonly string[]);
  for (const d of input.decisions) {
    const r = byId.get(d.id);
    if (!r) continue;
    yield csvRow([
      r.path,
      d.verdict,
      d.reason,
      input.plan.names.get(d.id) ?? '',
      d.groupId === null ? '' : String(d.groupId),
      String(groupSize.get(d.id) ?? 1),
      d.against === null ? '' : (byId.get(d.against)?.path ?? ''),
      hashToHex(r.hash),
      r.sharpness.toFixed(2),
      r.flatness.toFixed(3),
      String(r.width),
      String(r.height),
      String(r.bytes),
    ]);
  }
}

/**
 * RFC 4180 with the three pieces of hardening this fleet uses everywhere.
 *
 *  - Control characters become VISIBLE escapes, so one image is always exactly one physical line.
 *    A deliberate deviation from RFC 4180's quoted-newline rule: a report whose row count differs
 *    from its line count cannot be `wc -l`'d or `grep`'d, and a filename genuinely can contain a
 *    newline on macOS and Linux.
 *  - `;` forces quoting, because German, French and Spanish Excel split on it.
 *  - A leading `=`, `+`, `-` or `@` gets a `'` prefix. A filename is attacker-controllable when
 *    the input was someone else's ZIP, and CSV formula injection is a real path from "here is a
 *    report" to code execution on the machine that opens it.
 */
export function csvField(v: string): string {
  let s = '';
  for (let i = 0; i < v.length; i++) {
    const c = v.charCodeAt(i);
    if (c === 92) s += '\\\\';
    else if (c === 13) s += '\\r';
    else if (c === 10) s += '\\n';
    else if (c === 9) s += '\\t';
    else if (c < 32 || c === 127) s += '\\x' + c.toString(16).padStart(2, '0');
    else s += v[i];
  }
  if (/^[=+\-@]/.test(s)) s = "'" + s;
  if (/[",;]|^\s|\s$/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
  return s;
}

export function csvRow(fields: readonly string[]): string {
  return fields.map(csvField).join(',') + '\r\n';
}

/** UTF-8 BOM. On the CSV only — never on a caption file. */
export const CSV_BOM = '﻿';

/** The sidecar that makes `embeddings.f32` self-describing. */
export function embeddingManifest(
  ids: readonly number[],
  paths: readonly string[],
  dim: number,
  model: string,
): string {
  return JSON.stringify(
    {
      format: 'raw little-endian float32, row-major, C-contiguous',
      shape: [ids.length, dim],
      normalised: 'L2 (unit length) — cosine similarity is a plain dot product',
      model,
      note: 'np.fromfile("embeddings.f32", dtype="<f4").reshape(shape)',
      rows: paths,
    },
    null,
    2,
  );
}
